/**
 * Account deactivation — calm warning, optional reason, password confirm,
 * destructive submit that signs the user out. Wired: POST
 * /api/account/deactivate. Port of screens-account.jsx Deactivate.
 */
import { router } from 'expo-router';
import React from 'react';
import { View } from 'react-native';

import { space } from '@bero/tokens';
import { InfoNote } from '@/components/gate/bits';
import { goBack } from '@/features/account-admin/bits';
import { Icon } from '@/features/account-admin/icons';
import { BottomBar } from '@/features/community/bits';
import { useT } from '@/i18n';
import { api } from '@/lib/client';
import { useSession } from '@/lib/session';
import { useTheme } from '@/theme';
import { AppText, Button, Field, Input, Screen, TopBar } from '@/ui';

export default function DeactivateScreen() {
  const { colors } = useTheme();
  const { t } = useT();
  const { user, signOut } = useSession();
  const [reason, setReason] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [passwordError, setPasswordError] = React.useState<string | undefined>();
  const [saving, setSaving] = React.useState(false);
  const [notice, setNotice] = React.useState<string | null>(null);

  const flat = user?.flat?.code ?? t('da_your_flat');

  const submit = async () => {
    if (!password) {
      setPasswordError(t('da_password_error'));
      return;
    }
    setPasswordError(undefined);
    setSaving(true);
    setNotice(null);
    try {
      await api.post<unknown>('/api/account/deactivate', {
        password, reason: reason.trim() || undefined,
      });
      await signOut();
      router.replace('/login');
    } catch (err) {
      const status = (err as { status?: number }).status;
      setNotice(status === 401 || status === 403
        ? t('da_wrong_password')
        : (err as { error?: string }).error ?? t('da_generic_error'));
      setSaving(false);
    }
  };

  return (
    <Screen scroll={false}>
      <TopBar title={t('da_title')} onBack={goBack} />
      <Screen padded style={{ paddingTop: space.s6 }}>
        <Icon name="alert-triangle" size={40} color={colors.warning500} />
        <AppText variant="headingMd" style={{ marginTop: space.s4 }}>
          {t('da_heading')}
        </AppText>
        <AppText variant="bodyMd" tone="secondary" style={{ marginTop: space.s2 }}>
          {t('da_body', { flat })}
        </AppText>
        <View style={{ marginTop: space.s5, gap: space.s4 }}>
          <Field label={t('da_reason')}>
            <Input
              value={reason}
              onChangeText={setReason}
              placeholder={t('da_reason_ph')}
              multiline
              style={{ minHeight: 80, textAlignVertical: 'top' }}
            />
          </Field>
          <Field label={t('da_confirm_pw')} error={passwordError}>
            <Input value={password} onChangeText={setPassword} revealable placeholder="••••••••" hasError={!!passwordError} />
          </Field>
          {notice ? <InfoNote icon="info" tint={colors.danger500}>{notice}</InfoNote> : null}
        </View>
      </Screen>
      <BottomBar>
        <Button
          title={t('da_request')}
          kind="destructive"
          size="lg"
          loading={saving}
          onPress={() => { void submit(); }}
        />
      </BottomBar>
    </Screen>
  );
}
